import '../css/EditUserModal.less';

import React, {useState, FunctionComponent, useCallback, useRef} from 'react';
import _ from 'lodash';
import {
  Button,
  Input,
  Divider,
  Table,
  Modal,
  Dropdown,
} from 'semantic-ui-react';
import LegacyWBIcon from './elements/LegacyWBIcon';
import {
  useAddUserToOrganizationMutation,
  useRemoveUserFromOrganizationMutation,
  useUpdateOrganizationUserMutation,
} from '../generated/graphql';
import {toast} from 'react-toastify';
import {
  propagateErrorsContext,
  extractErrorMessageFromApolloError,
} from '../util/errors';
import {
  OrganizationFragment,
  OrganizationMemberFragment,
} from '../pages/BillingPage';
import {useViewer} from '../state/viewer/hooks';
import makeComp from '../util/profiler';

export interface EditUserModalProps {
  open: boolean;
  organization: OrganizationFragment | null;
  onAddUser(): Promise<any>;
  onRemoveUser(): Promise<any>;
  onUpdateOrganizationUser(): Promise<any>;
  onClose(): void;
}

const EditUserModal: FunctionComponent<EditUserModalProps> = makeComp(
  ({
    open,
    organization,
    onAddUser,
    onRemoveUser,
    onUpdateOrganizationUser,
    onClose,
  }) => {
    const viewer = useViewer();
    const [userName, setUserName] = useState('');
    const [teamsAddedTo, setTeamsAddedTo] = useState<string[]>([]);
    const [userOrgRole, setUserOrgRole] = useState('');
    const [addUserVisible, setAddUserVisible] = useState(false);
    const [
      userToRemove,
      setUserToRemove,
    ] = useState<OrganizationMemberFragment | null>(null);
    const [addUserToOrganizationMutation] = useAddUserToOrganizationMutation({
      context: propagateErrorsContext(),
    });
    const [
      removeUserFromOrganizationMutation,
    ] = useRemoveUserFromOrganizationMutation({
      context: propagateErrorsContext(),
    });
    const [
      userToUpdateRole,
      setUserToUpdateRole,
    ] = useState<OrganizationMemberFragment | null>(null);
    const [updateOrganizationUserMutation] = useUpdateOrganizationUserMutation({
      context: propagateErrorsContext(),
    });

    const inputRef = useRef<Input>(null);

    const close = useCallback(() => {
      onClose();
      setAddUserVisible(false);
      setUserName('');
      setTeamsAddedTo([]);
      setUserOrgRole('');
    }, [onClose]);

    const addUser = useCallback(async () => {
      if (organization == null) {
        return;
      }
      try {
        const addUserResult = await addUserToOrganizationMutation({
          variables: {
            userName,
            teams: teamsAddedTo,
            organizationId: organization.id,
            userOrgRole,
          },
        });

        if (addUserResult.errors) {
          toast(`Error adding user: ${addUserResult.errors}`);
        } else if (
          addUserResult.data &&
          addUserResult.data.addUserToOrganization?.success
        ) {
          await onAddUser();
          toast('Succesfully added user to organization.');
          close();
        }
      } catch (err) {
        const errMsg = extractErrorMessageFromApolloError(err) ?? err.message;
        toast(`Error adding user to organization: ${errMsg}`);
      }
    }, [
      onAddUser,
      close,
      addUserToOrganizationMutation,
      userName,
      teamsAddedTo,
      organization,
      userOrgRole,
    ]);

    const removeUser = useCallback(
      async (member: OrganizationMemberFragment) => {
        if (organization == null) {
          return;
        }
        try {
          const removeUserResult = await removeUserFromOrganizationMutation({
            variables: {
              userName: member.username,
              organizationId: organization.id,
            },
          });

          if (removeUserResult.errors) {
            toast(`Error removing user: ${removeUserResult.errors}`);
          } else if (
            removeUserResult.data &&
            removeUserResult.data.removeUserFromOrganization?.success
          ) {
            await onRemoveUser();
            toast('Successfully removed user from organization.');
            close();
          }
        } catch (err) {
          const errMsg = extractErrorMessageFromApolloError(err) ?? err.message;
          toast(`Error removing user from organization: ${errMsg}`);
        }
      },
      [onRemoveUser, close, removeUserFromOrganizationMutation, organization]
    );

    const updateUserRole = useCallback(
      async (member: OrganizationMemberFragment) => {
        if (organization == null) {
          return;
        }
        try {
          const updateUserRoleResult = await updateOrganizationUserMutation({
            variables: {
              userName: member.username,
              organizationId: organization.id,
              userOrgRole: member.role,
            },
          });
          if (updateUserRoleResult.errors) {
            toast(`Error updating user role: ${updateUserRoleResult.errors}`);
          } else if (
            updateUserRoleResult.data &&
            updateUserRoleResult.data.updateOrganizationUser?.success
          ) {
            await onUpdateOrganizationUser();
            toast('Successfully updated user role.');
            close();
          }
        } catch (err) {
          const errMsg = extractErrorMessageFromApolloError(err) ?? err.message;
          toast(`Error updating user: ${errMsg}`);
        }
      },
      [
        close,
        organization,
        updateOrganizationUserMutation,
        onUpdateOrganizationUser,
      ]
    );

    const members = organization?.members ?? [];
    const memberList = members.map((member, ind) => {
      let options;
      // if billing user must be admin or billing only
      if (member.username === organization?.billingUserName) {
        options = [
          {text: 'Billing Only', value: 'billing-only'},
          {text: 'Admin', value: 'admin'},
        ];
        // if non-billing user, must be member or admin
      } else {
        options = [
          {text: 'Member', value: 'member'},
          {text: 'Admin', value: 'admin'},
        ];
      }
      return (
        <Table.Row key={member.username}>
          <Table.Cell className="edit-user-modal__table-cell">
            {member.name}
          </Table.Cell>
          <Table.Cell className="edit-user-modal__table-cell">
            {member.username}
          </Table.Cell>
          <Table.Cell className="edit-user-modal__table-cell">
            {(member.teams?.edges ?? [])
              .map(e => e.node?.name ?? '')
              .join(', ')}
          </Table.Cell>
          <Table.Cell className="edit-user-modal__table-cell">
            <div>
              <Dropdown
                className="edit-user-modal__org-role-dropdown"
                data-test="edit-org-role-dropdown"
                data-test-index={`${ind}`}
                placeholder={member.role}
                selection
                options={options}
                onChange={(e, {value}) =>
                  setUserToUpdateRole({...member, role: value as string})
                }
                value={member.role}
              />
            </div>
          </Table.Cell>
          <Table.Cell>
            {member.username !== viewer?.username && (
              <LegacyWBIcon
                name="close"
                className="edit-user-modal__icon"
                onClick={() => setUserToRemove(member)}
              />
            )}
          </Table.Cell>
        </Table.Row>
      );
    });

    const teamsMap = members.reduce((acc, m) => {
      (m.teams?.edges ?? []).forEach(e => {
        const t = e.node;
        if (t != null) {
          acc[t.name] = true;
        }
      });
      return acc;
    }, {} as {[name: string]: true});

    const teams = _.sortBy(Object.keys(teamsMap));

    return (
      <>
        <Modal className="edit-user-modal" open={open} onClose={close}>
          <Modal.Header>Edit Organization</Modal.Header>
          <Modal.Content>
            <div className="edit-user-modal__info">
              Add and remove members from your organization.
            </div>
            {memberList.length > 0 && (
              <Table basic="very" className="edit-user-modal__table">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell className="edit-user-modal__table-header">
                      Name
                    </Table.HeaderCell>
                    <Table.HeaderCell className="edit-user-modal__table-header">
                      Username
                    </Table.HeaderCell>
                    <Table.HeaderCell className="edit-user-modal__table-header">
                      Teams
                    </Table.HeaderCell>
                    <Table.HeaderCell className="edit-user-modal__table-header">
                      Organization Role
                    </Table.HeaderCell>
                    <Table.HeaderCell />
                  </Table.Row>
                </Table.Header>
                <Table.Body>{memberList}</Table.Body>
              </Table>
            )}
            <Divider className="edit-user-modal__divider" />
            <form
              className="edit-user-modal__add-user"
              onSubmit={e => {
                e.preventDefault();
                addUser();
              }}>
              {addUserVisible ? (
                <>
                  <Input
                    ref={inputRef}
                    className="edit-user-modal__input"
                    data-test="add-user-input"
                    placeholder="Enter username"
                    onChange={(e, data) => setUserName(data.value)}
                    value={userName}
                  />
                  <Dropdown
                    className="edit-user-modal__dropdown"
                    data-test="add-user-team-input"
                    placeholder="Select team(s)"
                    fluid
                    multiple
                    search
                    selection
                    options={teams.map(t => ({text: t, value: t}))}
                    onChange={(e, {value}) =>
                      setTeamsAddedTo(value as string[])
                    }
                    value={teamsAddedTo}
                  />
                  <Dropdown
                    className="edit-user-modal__dropdown"
                    data-test="add-user-type-dropdown"
                    placeholder="User type"
                    fluid
                    search
                    selection
                    options={[
                      {text: 'Member', value: 'member'},
                      {text: 'Admin', value: 'admin'},
                    ]}
                    onChange={(e, {value}) => setUserOrgRole(value as string)}
                    value={userOrgRole}
                  />
                  <Button
                    className="ui button action-button"
                    data-test="submit-add-user"
                    disabled={userOrgRole === ''}>
                    Add
                  </Button>
                </>
              ) : (
                <div
                  className="edit-user-modal__add-user-link"
                  data-test="show-add-user"
                  onClick={() => {
                    setAddUserVisible(true);
                    setTimeout(() => inputRef.current?.focus());
                  }}>
                  + Add member
                </div>
              )}
            </form>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={close}>Close</Button>
          </Modal.Actions>
        </Modal>
        <Modal
          className="edit-user-modal"
          open={userToRemove != null}
          onClose={() => {
            setUserToRemove(null);
          }}>
          <Modal.Header>
            Are you sure you want to remove {userToRemove?.username} (
            {userToRemove?.email})? They will also be removed from teams they
            are currently a part of.
          </Modal.Header>
          <Modal.Actions>
            <Button
              color="red"
              onClick={() => {
                if (userToRemove == null) {
                  return;
                }
                removeUser(userToRemove);
                setUserToRemove(null);
              }}>
              Delete
            </Button>
            <Button
              onClick={() => {
                setUserToRemove(null);
              }}>
              Cancel
            </Button>
          </Modal.Actions>
        </Modal>
        <Modal
          className="edit-user-modal"
          open={userToUpdateRole != null}
          onClose={() => {
            setUserToUpdateRole(null);
          }}>
          <Modal.Header>Update Role</Modal.Header>
          <Modal.Content>
            Are you sure you want to update {userToUpdateRole?.username}'s role
            to {userToUpdateRole?.role}?
            {userToUpdateRole?.role === 'billing-only' &&
              ` Before you switch ${userToUpdateRole?.username} to a billing-only user,
             make sure there is another admin on each of your teams. ${userToUpdateRole?.username} 
             will lose access to all of the organization's teams.`}
          </Modal.Content>
          <Modal.Actions>
            <Button
              color="red"
              data-test="accept-update-user-role-button"
              onClick={() => {
                if (userToUpdateRole == null) {
                  return;
                }
                updateUserRole(userToUpdateRole);
                setUserToUpdateRole(null);
              }}>
              Update
            </Button>
            <Button
              onClick={() => {
                setUserToUpdateRole(null);
              }}>
              Cancel
            </Button>
          </Modal.Actions>
        </Modal>
      </>
    );
  },
  {id: 'EditUserModal'}
);

export default EditUserModal;
